/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import {
  ApiError,
  ApiErrorAction,
  HttpError,
  StateResource,
  TypedActionCreatorWithProps,
} from '@common/tech-shared';
import { Action, createAction, props } from '@ngrx/store';
import { Resource, ResourceUri } from '@ngxp/rest';
import { CommandListResource, CommandResource, CreateCommand, CreateCommandActionProps } from '../command.model';

export interface ConcurrentModificationProps {
  effectedResourceUri: ResourceUri;
}

export const publishConcurrentModificationAction: TypedActionCreatorWithProps<ConcurrentModificationProps> = createAction(
  '[Command/API] Concurrent Modification',
  props<ConcurrentModificationProps>(),
);

export interface CommandStateResourceProps {
  commandStateResource: StateResource<CommandResource>;
}

export interface LoadCommandListSuccessProps {
  commandList: CommandListResource;
}
export interface LoadCommandListProps {
  resource: Resource;
  linkRel: string;
  successAction: (commandList: CommandListResource) => LoadCommandListSuccessProps & Action;
  failureAction: (apiError: ApiError) => ApiErrorAction & Action;
}

export const loadCommandList: TypedActionCreatorWithProps<LoadCommandListProps> = createAction(
  '[Command] Load pending commands',
  props<LoadCommandListProps>(),
);

export interface UpdateCommandProps {
  relatedResourceUri: ResourceUri;
  command: CommandResource;
}

export interface CommandProps {
  command: CommandResource;
}

export interface SnackBarProps {
  command: CommandResource;
  createCommandActionProps: CreateCommandActionProps;
}

export interface PollCommandProps {
  command: CommandResource;
  createCommandActionProps?: CreateCommandActionProps;
}

export interface RevokeCommandFailureProps {
  command: CommandResource;
  error: ApiError | unknown;
}

export interface UpdateCreateCommandProps {
  createCommandActionProps: CreateCommandActionProps;
  command: CommandResource;
}

export interface CreateCommandSuccessProps {
  createCommandActionProps: CreateCommandActionProps;
  command: CommandResource;
}

export interface CreateCommandFailureProps {
  createCommandActionProps: CreateCommandActionProps;
  command: CreateCommand;
  error: HttpError | string;
}

const createCommand: TypedActionCreatorWithProps<CreateCommandActionProps> = createAction(
  '[Command] Create command',
  props<CreateCommandActionProps>(),
);
const updateCreateCommand: TypedActionCreatorWithProps<UpdateCreateCommandProps> = createAction(
  '[Command] Update create command',
  props<UpdateCreateCommandProps>(),
);
const createCommandSuccess: TypedActionCreatorWithProps<CreateCommandSuccessProps> = createAction(
  '[Command] Create command Success',
  props<CreateCommandSuccessProps>(),
);
const createCommandFailure: TypedActionCreatorWithProps<CreateCommandFailureProps> = createAction(
  '[Command] Create command Failure',
  props<CreateCommandFailureProps>(),
);

const showRevokeSnackbar: TypedActionCreatorWithProps<CommandProps> = createAction(
  '[Command] Show Revoke Snackbar',
  props<CommandProps>(),
);
const showSnackbar: TypedActionCreatorWithProps<SnackBarProps> = createAction('[Command] Show Snackbar', props<SnackBarProps>());

const showErrorSnackbar: TypedActionCreatorWithProps<SnackBarProps> = createAction(
  '[Command] Show Error Snackbar',
  props<SnackBarProps>(),
);

const revokeCommand: TypedActionCreatorWithProps<CommandProps> = createAction('[Command] Revoke command', props<CommandProps>());
const revokeCommandSuccess: TypedActionCreatorWithProps<CommandProps> = createAction(
  '[Command] Revoke command Success',
  props<CommandProps>(),
);
const revokeCommandFailure: TypedActionCreatorWithProps<RevokeCommandFailureProps> = createAction(
  '[Command] Revoke command Failure',
  props<RevokeCommandFailureProps>(),
);
const pollRevokedCommand: TypedActionCreatorWithProps<PollCommandProps> = createAction(
  '[Command] Poll revoked command',
  props<PollCommandProps>(),
);

export const CommandActions = {
  publishConcurrentModificationAction,
  loadCommandList,
  createCommand,
  updateCreateCommand,
  createCommandSuccess,
  createCommandFailure,
  showRevokeSnackbar,
  showSnackbar,
  showErrorSnackbar,
  revokeCommand,
  revokeCommandSuccess,
  revokeCommandFailure,
  pollRevokedCommand,
};
